import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Seo from '@/components/feature/Seo';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, hotArticles } from '@/mocks/articles';
import { categories, getCategory } from '@/mocks/categories';

type SortKey = 'latest' | 'hot' | 'read';

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'latest', label: '最新' },
  { key: 'hot', label: '最热' },
  { key: 'read', label: '阅读时长' },
];

export default function CategoryPage() {
  const { slug = 'live' } = useParams();
  const category = getCategory(slug);
  const [keyword, setKeyword] = useState('');
  const [sort, setSort] = useState<SortKey>('latest');

  const list = useMemo(() => {
    const base = category
      ? articles.filter((item) => item.category === category.slug)
      : articles;
    const filtered = keyword.trim()
      ? base.filter((item) =>
          [item.title, item.excerpt, item.tags.join(' ')].some((field) =>
            field.toLowerCase().includes(keyword.trim().toLowerCase()),
          ),
        )
      : base;
    return [...filtered].sort((a, b) => {
      if (sort === 'hot') return b.views - a.views;
      if (sort === 'read') return b.readTime - a.readTime;
      return a.date < b.date ? 1 : -1;
    });
  }, [category, keyword, sort]);

  const categoryName = category ? category.name : '全部反差档案';
  const title = `${categoryName}｜反差现场 - 网红反差真实案例归档`;
  const description = category
    ? `${category.desc} 汇集「反差现场」${categoryName}栏目下的现场素材，持续更新网红反差台上台下、镜头前后的真实案例。`
    : '反差现场全部栏目档案汇总，包含直播翻车现场、台下偶遇实录、粉丝爆料与私下被拍四类现场素材。';
  const keywords = category
    ? `${category.name},网红反差,网红反差台上台下,${category.name}案例`
    : '网红反差,网红反差台上台下,网红反差真实性格,网红反差镜头前后';

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: categoryName,
      description,
      inLanguage: 'zh-CN',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
        { '@type': 'ListItem', position: 2, name: categoryName, item: `/category/${slug}` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `${categoryName} 内容列表`,
      itemListElement: list.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `/article/${item.slug}`,
        name: item.title,
      })),
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Seo
        title={title}
        description={description}
        keywords={keywords}
        path={`/category/${slug}`}
        jsonLd={jsonLd}
      />
      <SiteHeader />

      <main className="flex-1 pt-16 md:pt-20">
        <div className="border-b border-background-200 bg-background-100/60">
          <div className="mx-auto max-w-content px-4 md:px-8 py-8 md:py-12">
            <nav aria-label="面包屑" className="flex items-center flex-wrap gap-2 text-xs text-foreground-500 font-label">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
              <span>反差档案</span>
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
              <span className="text-foreground-800">{categoryName}</span>
            </nav>

            <div className="mt-5 flex items-start gap-4">
              {category && (
                <span className="w-12 h-12 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
                  <i className={`${category.icon} text-2xl leading-none`} aria-hidden="true" />
                </span>
              )}
              <div>
                <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950">
                  {categoryName}
                </h1>
                <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600 max-w-3xl">
                  {category
                    ? category.desc
                    : '四大栏目内容统一归档，你也可以在左侧切换到你关心的具体方向。'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-content px-4 md:px-8 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            <aside className="lg:col-span-3 order-2 lg:order-1">
              <div className="lg:sticky lg:top-24 space-y-6">
                <nav aria-label="栏目切换" className="bg-background-100 border border-background-200 rounded-lg p-4">
                  <h2 className="font-heading text-sm text-foreground-950 mb-3">全部栏目</h2>
                  <ul className="space-y-1">
                    {categories.map((cat) => (
                      <li key={cat.slug}>
                        <Link
                          to={`/category/${cat.slug}`}
                          className={`flex items-center justify-between px-3 py-2.5 rounded-md text-sm transition-colors ${
                            cat.slug === slug
                              ? 'bg-primary-500 text-background-50'
                              : 'text-foreground-700 hover:bg-background-200'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <i className={`${cat.icon} text-base leading-none`} aria-hidden="true" />
                            {cat.name}
                          </span>
                          <span className="text-xs opacity-70">
                            {articles.filter((a) => a.category === cat.slug).length}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>

                <div className="bg-background-100 border border-background-200 rounded-lg p-4">
                  <h2 className="flex items-center gap-2 font-heading text-sm text-foreground-950 mb-3">
                    <i className="ri-fire-line text-primary-500" aria-hidden="true" />
                    热门推荐
                  </h2>
                  <div className="space-y-4">
                    {hotArticles.slice(0, 3).map((item) => (
                      <ArticleCard key={item.id} article={item} variant="compact" />
                    ))}
                  </div>
                </div>
              </div>
            </aside>

            <div className="lg:col-span-9 order-1 lg:order-2">
              <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-6">
                <div className="relative w-full md:max-w-xs">
                  <i
                    className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base pointer-events-none"
                    aria-hidden="true"
                  />
                  <input
                    type="search"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="搜索标题、标签或关键词"
                    aria-label="搜索档案"
                    className="w-full pl-10 pr-4 py-2.5 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
                  />
                </div>
                <div className="flex items-center gap-1 bg-background-100 border border-background-200 rounded-full p-1 w-fit">
                  {sortOptions.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setSort(option.key)}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-label transition-colors cursor-pointer whitespace-nowrap ${
                        sort === option.key
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-600 hover:text-foreground-900'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <p className="mb-4 text-xs text-foreground-500 font-label">
                共 {list.length} 篇内容{keyword && ` · 关键词「${keyword}」`}
              </p>

              {list.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                  {list.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center border border-dashed border-background-300 rounded-lg">
                  <i className="ri-inbox-archive-line text-4xl text-foreground-300" aria-hidden="true" />
                  <p className="mt-4 text-sm text-foreground-600">没有找到匹配的内容</p>
                  <button
                    type="button"
                    onClick={() => setKeyword('')}
                    className="mt-4 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-label cursor-pointer hover:bg-primary-600 transition-colors"
                  >
                    清空搜索
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}